import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import {
  listDictionaryEntries,
  normalizeDictionaryTermKey,
  provisionDictionaryVariant,
} from 'services/dictionary/DictionaryService';

function getFieldKey(field) {
  return String(field?.name || field?.key || '').trim();
}

function getDefaultSelectedFieldKeys(fields) {
  return (Array.isArray(fields) ? fields : [])
    .map((field) => ({
      fieldKey: getFieldKey(field),
      includeByDefault: field?.includeByDefault !== false,
    }))
    .filter((entry) => entry.fieldKey)
    .filter((entry) => entry.includeByDefault)
    .map((entry) => entry.fieldKey);
}

function buildProvisionSuccessMessage(created, selectedFieldKeys) {
  const label = String(created?.label || 'Type').trim();
  const importedViewsCount = Number(created?.importedViewsCount || 0);
  const skippedViewsCount = Number(created?.skippedViewsCount || 0);
  const importedAutomationsCount = Number(created?.importedAutomationsCount || 0);
  const skippedAutomationsCount = Number(created?.skippedAutomationsCount || 0);
  const importedFieldsCount = Array.isArray(selectedFieldKeys) ? selectedFieldKeys.length : 0;
  const details = [
    `${importedFieldsCount} fields`,
    `${importedViewsCount} views${skippedViewsCount > 0 ? `, ${skippedViewsCount} skipped` : ''}`,
    `${importedAutomationsCount} automations${skippedAutomationsCount > 0 ? `, ${skippedAutomationsCount} skipped` : ''}`,
  ];

  return `Imported ${label}. Added ${details.join(' · ')}.`;
}

export function useDictionarySection({
  isOpen,
  selectedTab,
  dictionaryTabValue,
  workspaceKey,
  availableTypes,
  onTypeSelect,
  onClose,
}) {
  const [query, setQuery] = useState('');
  const [terms, setTerms] = useState([]);
  const [selectedTermKey, setSelectedTermKey] = useState('');
  const [selectedVariantKey, setSelectedVariantKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [provisioningVariantKey, setProvisioningVariantKey] = useState('');
  const [selectedFieldKeys, setSelectedFieldKeys] = useState([]);

  const workspaceLabelSet = useMemo(() => {
    const labels = new Set();

    (availableTypes || []).forEach((typeDef) => {
      if (!typeDef || typeDef.system === true) return;
      const label = String(typeDef.label || '').trim();
      const key = normalizeDictionaryTermKey(label);
      if (key) labels.add(key);
      const labelLower = String(typeDef.labelLower || '').trim().toLowerCase();
      if (labelLower) labels.add(normalizeDictionaryTermKey(labelLower));
    });

    return labels;
  }, [availableTypes]);

  const normalizedQuery = String(query || '').trim().toLowerCase();

  const visibleTerms = useMemo(() => {
    return (terms || []).filter((term) => {
      const termKey = normalizeDictionaryTermKey(term.key || term.label);
      if (!termKey) return false;
      if (workspaceLabelSet.has(termKey)) return false;

      if (!normalizedQuery) return true;
      const label = String(term.label || '').trim().toLowerCase();
      const pluralLabel = String(term.labelPlural || '').trim().toLowerCase();
      return label.includes(normalizedQuery)
        || pluralLabel.includes(normalizedQuery)
        || termKey.includes(normalizedQuery);
    });
  }, [normalizedQuery, terms, workspaceLabelSet]);

  const selectedTerm = useMemo(() => {
    if (!selectedTermKey) return null;
    return visibleTerms.find((term) => term.key === selectedTermKey) || null;
  }, [selectedTermKey, visibleTerms]);

  const selectedVariant = useMemo(() => {
    if (!selectedTerm || !selectedVariantKey) return null;
    return (selectedTerm.variants || []).find((variant) => variant.key === selectedVariantKey) || null;
  }, [selectedTerm, selectedVariantKey]);

  const isVariantStep = Boolean(selectedTerm && !selectedVariant);
  const isVariantPreviewStep = Boolean(selectedTerm && selectedVariant);

  const loadTerms = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const rows = await listDictionaryEntries({ includeVariants: true });
      setTerms(rows);
    } catch (loadError) {
      setError(loadError?.message || 'Failed to load dictionary entries.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || selectedTab !== dictionaryTabValue) return;
    loadTerms();
  }, [dictionaryTabValue, isOpen, loadTerms, selectedTab]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSelectedTermKey('');
      setSelectedVariantKey('');
      setSelectedFieldKeys([]);
      setError('');
      setIsProvisioning(false);
      setProvisioningVariantKey('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!selectedTermKey) return;
    const exists = visibleTerms.some((term) => term.key === selectedTermKey);
    if (!exists) {
      setSelectedTermKey('');
      setSelectedVariantKey('');
    }
  }, [selectedTermKey, visibleTerms]);

  useEffect(() => {
    if (!selectedTerm || !selectedVariantKey) return;
    const exists = (selectedTerm.variants || []).some((variant) => variant.key === selectedVariantKey);
    if (!exists) setSelectedVariantKey('');
  }, [selectedTerm, selectedVariantKey]);

  useEffect(() => {
    const nextFields = selectedVariant?.schema?.fields || [];
    setSelectedFieldKeys(getDefaultSelectedFieldKeys(nextFields));
  }, [selectedVariant?.key, selectedVariant?.schema?.fields]);

  const handleProvisionVariant = useCallback(async (termKey, variantKey, nextSelectedFieldKeys = []) => {
    if (!workspaceKey || !termKey || !variantKey) return;

    setError('');
    setIsProvisioning(true);
    setProvisioningVariantKey(variantKey);

    try {
      const created = await provisionDictionaryVariant({
        workspaceKey,
        termKey,
        variantKey,
        selectedFieldKeys: nextSelectedFieldKeys,
      });

      if (created?.key && typeof onTypeSelect === 'function') {
        onTypeSelect(created.key, created);
      }

      toast.success(buildProvisionSuccessMessage(created, nextSelectedFieldKeys));

      if (typeof onClose === 'function') {
        onClose();
      }
    } catch (provisionError) {
      setError(provisionError?.message || 'Failed to provision dictionary variant.');
      await loadTerms();
    } finally {
      setIsProvisioning(false);
      setProvisioningVariantKey('');
    }
  }, [loadTerms, onClose, onTypeSelect, workspaceKey]);

  const handleSelectTerm = useCallback((term) => {
    if (!term) return;

    const variants = Array.isArray(term.variants) ? term.variants : [];
    setSelectedTermKey(term.key);
    setSelectedVariantKey(variants.length === 1 ? variants[0]?.key || '' : '');
  }, []);

  const handleSelectVariant = useCallback((variant) => {
    if (!variant) return;
    setSelectedVariantKey(variant.key || '');
  }, []);

  const handleBackToTerms = useCallback(() => {
    setSelectedTermKey('');
    setSelectedVariantKey('');
  }, []);

  const handleBackToVariants = useCallback(() => {
    const variantCount = (selectedTerm?.variants || []).length;
    if (variantCount > 1) {
      setSelectedVariantKey('');
      return;
    }

    setSelectedTermKey('');
    setSelectedVariantKey('');
  }, [selectedTerm]);

  const handleUseSelectedVariant = useCallback(async () => {
    if (!selectedTerm || !selectedVariant) return;
    await handleProvisionVariant(selectedTerm.key, selectedVariant.key, selectedFieldKeys);
  }, [handleProvisionVariant, selectedFieldKeys, selectedTerm, selectedVariant]);

  const handleToggleSelectedField = useCallback((fieldKey) => {
    const normalizedFieldKey = String(fieldKey || '').trim();
    if (!normalizedFieldKey) return;

    setSelectedFieldKeys((prev) => {
      if (prev.includes(normalizedFieldKey)) {
        return prev.filter((entry) => entry !== normalizedFieldKey);
      }

      return [...prev, normalizedFieldKey];
    });
  }, []);

  return {
    query,
    setQuery,
    loading,
    error,
    terms: visibleTerms,
    selectedTerm,
    selectedVariant,
    isVariantStep,
    isVariantPreviewStep,
    isProvisioning,
    provisioningVariantKey,
    selectedFieldKeys,
    workspaceKey,
    handleSelectTerm,
    handleSelectVariant,
    handleBackToTerms,
    handleBackToVariants,
    handleToggleSelectedField,
    handleUseSelectedVariant,
  };
}
